/**
 * @typedef { "all" | "pending" | "returned" | "published" } TabType
 * @typedef { "provinces" | "province" | "lgu" } SelectionLevel
 * @typedef {{ lgu, province, month, year, offset }} SelectionReference 
 */

import Client from "../../../Client";
import Exceptions from "../../../Exceptions";
import { getCurrentMonth, setCurrentMonth } from "../../../misc/date";
import { MainRouter } from "../../../routes";
import { getSamedayAccountStatus, getSamedayLGUStatus, getSamedayPending, getSamedayProvinceStatus, getSamedayPublished, getSamedayReturned } from "../../../services/api/data/sameday/status";
import { getLGUList, getProvinceList } from "../../../services/api/meta";
import { AlertDrawerController } from "../../../views/sections/sidebar/AlertDrawer";
import { Notifier } from "../../conductor";


export class SamedayStatusController {

    /**
     * 
     * @param {{ tab: TabType, level: SelectionLevel, ref: SelectionReference }} options 
     */
    constructor(options) {
        this.currentTab = options.tab || "all";
        this.currentLevel = options.level;

        this.reference = options.ref;

        this.notifiers = {
            loading: new Notifier(true),
            loaded: new Notifier(false),
            error: new Notifier(null),
            alert: new AlertDrawerController(),
            changingTab: new Notifier(false)
        };

        const self = this;
        this.status = {
            get loading() { return self.notifiers.loading.get() },
            get error() { return self.notifiers.error.get() },
            get changingTab() { return self.notifiers.changingTab.get() }
        };

        this.data = {};

        this.initialize();
    }

    async initialize() {
        Client.ensureSignedIn();
        if(this.isInitialized) return; this.isInitialized = true;

        /** @type {TabType[]} */
        this.tabs = {
            lgu: [ "all", "returned", "published" ],
            province: [ "all", "pending", "returned", "published" ],
            reviewer: [ "all", "pending", "published" ],
            admin: [ "all", "pending", "published" ],
        }[Client.account.role] || [];

        try {
            await this.loadData();
            this.notifiers.loaded.update(true);
        } catch(e) {
            // if(e.unauthorized) return this.notifiers.unauthorized.update(true);
            this.notifiers.error.update(e);
            console.error(e);
        }

        this.notifiers.loading.update(false);
    }


    async loadData() {
        let { lgu, province, month, year, offset } = this.reference;

        this.data.status = await getSamedayAccountStatus();

        switch(this.currentTab) {
            case "all":
                switch(this.currentLevel) {
                    case "provinces":
                        this.data.provinces = await getProvinceList();
                    break;
                    case "province":
                        this.data.province = await getSamedayProvinceStatus({ province, month, year });
                    break;
                    case "lgu":
                        this.data.lgu = await getSamedayLGUStatus({ lgu, month, year });
                    break;
                    default:            
                }
            break;
            case "pending":
                this.data.pending = await getSamedayPending();
            break;
            case "returned":
                this.data.returned = await getSamedayReturned();
            break;
            case "published":
                const filter = { month, year, offset, limit: 10 };
                if(lgu) filter.lgu = lgu;
                else if(province) filter.province = province;

                this.data.published = await getSamedayPublished(filter);
                
                const dropdownOptions = {};
                const { role, role_specific } = Client.account;

                if(role === "province" && !province) province = role_specific; 
                if(role !== "lgu" && province) {
                    dropdownOptions.lgus = [ 
                        { label: "All LGUs", value: 0 }, 
                        ...(await getLGUList(province)).map(lgu => ({ value: lgu.id, label: lgu.name }))
                    ];
                }

                if([ "admin", "reviewer" ].includes(role)) dropdownOptions.provinces = 
                    [ { label: "All Provinces", value: 0 }, 
                    ...(await getProvinceList()).map(province => ({ value: province.id, label: province.name })) ];    
                
                this.data.dropdownOptions = dropdownOptions;
            break;

            default:
        }
    }

    /**
     * @param {{ tab, level, province, lgu, month, year }} options 
     * @returns { String }
     */
    getPath(options) {
        const basePath = {
            all: "/forms/sameday/all/{level}?m={month}&y={year}",
            pending: "/forms/sameday/pending",
            returned: "/forms/sameday/returned",
            published: "/forms/sameday/published?m={month}&y={year}&o={offset}",
        }[options.tab];

        if(!basePath) return "#";

        let path = basePath;
        for(const key in options) path = path.replace(`{${key}}`, options[key]);

        if(["all", "published"].includes(options.tab)) {
            if(options.province) path += `&p=${options.province}`;
            if(options.lgu) path += `&l=${options.lgu}`;
        }

        return path;
    }

    pushCurrentPath() {
        MainRouter.pushPath(this.getPath({ tab: this.currentTab, level: this.currentLevel, ...this.reference }));
    }

    async changeDate(month, year) {
        this.reference.month = month;
        this.reference.year = year;
        setCurrentMonth({ month, year });

        await this.refresh();
        this.pushCurrentPath();
    }

    async refresh(closeAlert = true) {
        const { loading, loaded, error, alert } = this.notifiers;
        loading.update(true);
        error.update(null);

        if(closeAlert) alert.close();

        try {
            await this.loadData();
            loaded.update();
        } catch(e) {
            const semantics = Exceptions.getARESemantics(e);
            error.update(semantics);
            // alert.alert(semantics.message, { status: "error", title: semantics.title, retry: () => this.refresh() });
        }

        loading.update(false);
    }

    async reloadTabData() {
        const {  changingTab, error } = this.notifiers;
        
        changingTab.update(true);
        
        try {
            await this.loadData();
        } catch(e) {
            const semantics = Exceptions.getARESemantics(e);
            error.update(semantics);
            // alert.alert(semantics.message, { status: "error", title: semantics.title, retry: () => this.refresh() });
        }

        changingTab.update(false);
    }

    async gotoTab(tab) {
        if(tab === "all" && !this.currentLevel) {
            const { role, role_specific } = Client.account;
            switch(role) {
                case "admin":
                    this.currentLevel = "provinces";
                break;
                case "reviewer":
                    this.currentLevel = "provinces";
                break;
                case "province":
                    this.currentLevel = "province";
                    this.reference.province = role_specific;
                break;
                case "lgu":
                    this.currentLevel = "lgu";
                    this.reference.lgu = role_specific;
                break;
                default:
                
            }
        }

        if(!this.reference.month) this.reference = { ...this.reference, ...getCurrentMonth() };
        if(tab === "published" && this.currentTab !== "published") {
            delete this.reference.lgu;
            delete this.reference.province;
        }
        MainRouter.goto(this.getPath({ tab, level: this.currentLevel, ...this.reference }));
        // this.currentTab = tab;
        // await this.reloadTabData();
        // this.pushCurrentPath();
    }

    async gotoLevel(level, data = {}) {
        this.reference = { ...this.reference, ...data };

        if(!this.reference.month) this.reference = { ...this.reference, ...getCurrentMonth() };
        MainRouter.goto(this.getPath({ tab: this.currentTab, level, ...this.reference }));
        // this.currentLevel = level;
        

        // await this.reloadTabData();
        // this.pushCurrentPath();
    }

    async changePublishedOffset(offset) {
        this.reference.offset = offset;
        MainRouter.goto(this.getPath({ tab: "published", ...this.reference }));
    }

    async changePublishedProvince(province) {
        this.reference.province = province;
        delete this.reference.lgu;
        MainRouter.goto(this.getPath({ tab: "published", ...this.reference }));
    }

    async changePublishedLGU(lgu) {
        this.reference.lgu = lgu;
        MainRouter.goto(this.getPath({ tab: "published", ...this.reference }));
    }


    async viewReport(id) { MainRouter.goto(`/forms/sameday/report/${id}`); }
    async viewDraft({ lgu, month, year }) { 
        MainRouter.goto(`/forms/sameday/report/draft?l=${lgu}&m=${month}&y=${year}`); 
    }
}